/**
 * Security Summary dashboard types — issue #819, Phase 3.
 *
 * The payload returned by GET /dashboard/security/summary. Grounded
 * in CVSS v3 severity bands, CWE categorisation, and the OWASP Top
 * 10:2025 list. Wraps data from a `SecurityFindingAdapter` that
 * talks to SAST/DAST/SCA/PTaaS backends (Snyk, Semgrep, Checkmarx,
 * Orca, Bugcrowd, Bishop Fox, NetSPI, …).
 *
 * The #819 signature section is "user experiences as attack
 * gateways": every security finding is joined to the flows that
 * traverse the affected endpoint, so the user can see which
 * experiences a breach could travel through.
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────
// Severity + finding source taxonomy.
// ─────────────────────────────────────────────────────────────────
export const FindingSeveritySchema = z.enum([
    'critical', // CVSS ≥ 9.0
    'high', // CVSS 7.0–8.9
    'medium', // CVSS 4.0–6.9
    'low', // CVSS 0.1–3.9
    'info',
]);
export const FindingSourceSchema = z.enum([
    'sast', // static application security testing
    'dast', // dynamic (runtime) scanning
    'sca', // software composition analysis (deps / CVEs)
    'pentest', // continuous pen-test
    'threat-model',
    'secret-scan',
]);
export const DataClassificationSchema = z.enum([
    'pii', // names, emails, addresses
    'pci', // payment card data
    'phi', // protected health information
    'auth', // JWTs, API keys, credentials
    'none',
]);
// ─────────────────────────────────────────────────────────────────
// Core finding. One row per discovered vulnerability.
// ─────────────────────────────────────────────────────────────────
export const SecurityFindingSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** Human-readable one-line description for the row body. */
    description: z.string().optional(),
    severity: FindingSeveritySchema,
    /** CVSS v3 base score 0..10. */
    cvss: z.number().min(0).max(10),
    /** MITRE CWE identifier, e.g. "CWE-79" (XSS), "CWE-89" (SQLi). */
    cweId: z.string().optional(),
    /** OWASP Top 10:2025 category id (A01..A10). */
    owaspTop10: z.string().optional(),
    source: FindingSourceSchema,
    /** When the finding was first observed. ISO string. */
    discoveredAt: z.string(),
    /** Status: 'open' | 'acknowledged' | 'in-progress' | 'closed'. */
    status: z.enum(['open', 'acknowledged', 'in-progress', 'closed']),
    /** SLA clock: remediation deadline ISO. */
    slaDeadline: z.string().nullable(),
    /** Did we hit the SLA target when this was/is resolved? */
    slaBreached: z.boolean().default(false),
    /** Where in the code (if any). */
    filePath: z.string().optional(),
    lineStart: z.number().int().optional(),
    lineEnd: z.number().int().optional(),
    /** Which backend endpoint this attaches to (if any). */
    endpointId: z.string().optional(),
    /** User experiences touched — derived from graph traversal. */
    affectedUserExperienceIds: z.array(z.string()).default([]),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// SBOM rows — one per dependency with aggregated CVE counts.
// ─────────────────────────────────────────────────────────────────
export const SbomRowSchema = z.object({
    name: z.string(),
    version: z.string(),
    /** How many open CVEs affect this dep. */
    openCveCount: z.number().int().nonnegative(),
    /** Worst severity across the open CVEs. */
    worstSeverity: FindingSeveritySchema,
    /** Count of our own modules that import this dep. */
    consumers: z.number().int().nonnegative(),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// Pen-test findings — manual human-discovered issues.
// ─────────────────────────────────────────────────────────────────
export const PenTestFindingSchema = z.object({
    id: z.string(),
    title: z.string(),
    severity: FindingSeveritySchema,
    reporter: z.string(),
    reportedAt: z.string(),
    status: z.enum(['reported', 'triaging', 'fixing', 'validating', 'closed']),
    /** Link (relative or absolute) to the reproduction report. */
    reportUrl: z.string().optional(),
    affectedUserExperienceIds: z.array(z.string()).default([]),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// Threat model coverage.
// ─────────────────────────────────────────────────────────────────
export const ThreatModelStatusSchema = z.enum([
    'none', // no threat model exists for this UX
    'draft',
    'reviewed',
    'stale', // older than N days (e.g. 180d)
]);
export const ThreatModelRowSchema = z.object({
    userExperienceId: z.string(),
    userExperienceName: z.string(),
    status: ThreatModelStatusSchema,
    lastReviewedAt: z.string().nullable(),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// Attack surface — endpoint-level inventory joined with findings.
// ─────────────────────────────────────────────────────────────────
export const AttackSurfaceRowSchema = z.object({
    endpointId: z.string(),
    endpointName: z.string(),
    framework: z.string().optional(),
    /** Whether the endpoint requires auth (from the analyzer). */
    authenticated: z.boolean(),
    /** Derived data classification of the data the endpoint touches. */
    dataClass: DataClassificationSchema,
    /** Worst finding severity attached to this endpoint. */
    worstFindingSeverity: FindingSeveritySchema.nullable(),
    /** Count of open findings on this endpoint. */
    openFindingCount: z.number().int().nonnegative(),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// The signature section: user-experience as attack gateway.
// ─────────────────────────────────────────────────────────────────
export const UserExperienceGatewayRowSchema = z.object({
    userExperienceId: z.string(),
    userExperienceName: z.string(),
    projectId: z.string(),
    projectName: z.string(),
    /** Distinct endpoints the flow traverses. */
    endpointsTouched: z.number().int().nonnegative(),
    /** Worst finding severity that intersects this flow. */
    worstFindingSeverity: FindingSeveritySchema.nullable(),
    /** Open findings that sit on any endpoint in this flow. */
    openFindingCount: z.number().int().nonnegative(),
    /** Data classification the flow handles. */
    dataClass: DataClassificationSchema,
    threatModel: ThreatModelStatusSchema,
    /** Composite 0..100 — higher = more dangerous gateway. */
    gatewayRiskScore: z.number().min(0).max(100),
});
// ─────────────────────────────────────────────────────────────────
// Per-project adapter status.
// ─────────────────────────────────────────────────────────────────
export const SecurityAdapterKindSchema = z.enum([
    'mock',
    'snyk',
    'semgrep',
    'checkmarx',
    'orca',
    'bugcrowd',
    'bishopfox',
    'netspi',
    'none',
]);
export const SecurityAdapterStatusSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    adapter: SecurityAdapterKindSchema,
    reachable: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────
// Distribution buckets (severity × age, CWE top-N, OWASP Top 10).
// ─────────────────────────────────────────────────────────────────
export const SeverityAgeBucketSchema = z.object({
    severity: FindingSeveritySchema,
    /** Rough age bucket: '<7d' / '7-30d' / '30-90d' / '>90d'. */
    age: z.enum(['<7d', '7-30d', '30-90d', '>90d']),
    count: z.number().int().nonnegative(),
});
export const CweDistributionRowSchema = z.object({
    cweId: z.string(),
    cweName: z.string().optional(),
    count: z.number().int().nonnegative(),
});
export const OwaspCoverageRowSchema = z.object({
    /** 'A01' ... 'A10'. */
    category: z.string(),
    name: z.string(),
    count: z.number().int().nonnegative(),
});
// ─────────────────────────────────────────────────────────────────
// Top-level payload.
// ─────────────────────────────────────────────────────────────────
export const SecurityTilesSchema = z.object({
    openCritical: z.number().int().nonnegative(),
    openHigh: z.number().int().nonnegative(),
    /** Mean days to close findings, banded by severity. */
    mttrDaysBySeverity: z.object({
        critical: z.number().nonnegative(),
        high: z.number().nonnegative(),
        medium: z.number().nonnegative(),
        low: z.number().nonnegative(),
        info: z.number().nonnegative(),
    }),
    /** Count of user experiences that pass through ≥1 endpoint with an open High/Critical finding. */
    userExperiencesAtRisk: z.number().int().nonnegative(),
    /** Publicly reachable endpoints (no auth + exposed). */
    attackSurfaceOpen: z.number().int().nonnegative(),
    /** Fraction of findings remediated within policy SLA. */
    slaCompliance: z.number().min(0).max(1),
});
export const SecuritySummaryPayloadSchema = z.object({
    schemaVersion: z.literal(1),
    generatedAt: z.string(),
    projectId: z.string().nullable(),
    projectCount: z.number().int().nonnegative(),
    adaptersConfigured: z.number().int().nonnegative(),
    adaptersMocked: z.number().int().nonnegative(),
    tiles: SecurityTilesSchema,
    /** Signature section. */
    userExperienceGateways: z.array(UserExperienceGatewayRowSchema),
    userExperienceGatewaysTruncated: z.boolean().default(false),
    userExperienceGatewaysTotal: z.number().int().nonnegative().default(0),
    /** Raw findings, sorted worst-first. Capped. */
    findings: z.array(SecurityFindingSchema),
    findingsTruncated: z.boolean().default(false),
    findingsTotal: z.number().int().nonnegative().default(0),
    /** Severity + age distribution for the stacked bar chart. */
    severityAgeBuckets: z.array(SeverityAgeBucketSchema),
    /** Top-N CWE categories across findings. */
    cweDistribution: z.array(CweDistributionRowSchema),
    /** OWASP Top 10:2025 coverage (one row per category). */
    owaspCoverage: z.array(OwaspCoverageRowSchema),
    /** SBOM view with dependency CVE counts. */
    sbom: z.array(SbomRowSchema),
    /** Pen-test findings timeline. */
    penTestFindings: z.array(PenTestFindingSchema),
    /** Threat-model coverage per user experience. */
    threatModels: z.array(ThreatModelRowSchema),
    /** Attack surface inventory. */
    attackSurface: z.array(AttackSurfaceRowSchema),
    adapterStatus: z.array(SecurityAdapterStatusSchema),
});
//# sourceMappingURL=securitySummary.js.map